import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './ImageViewer.css';
import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaSearch, FaSearchMinus, FaSearchPlus } from 'react-icons/fa';

const ImageViewer = ({ src, alt, isOpen, onClose, downloadFilename }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPosition, setStartDragPosition] = useState({ x: 0, y: 0 });
  const [startImagePosition, setStartImagePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Réinitialiser le zoom et la position quand une nouvelle image est chargée
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      
      // Bloquer le scroll sur le body quand le visualiseur est ouvert
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      // Rétablir le scroll quand le visualiseur est fermé
      document.body.style.overflow = 'auto';
    };
  }, [src, isOpen]);

  // Gérer la fermeture avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Gérer le zoom avec la molette de la souris
  const handleWheel = (e) => {
    if (!isOpen) return;
    
    e.preventDefault();
    
    const scaleFactor = 0.1;
    const newScale = e.deltaY < 0 
      ? Math.min(scale + scaleFactor, 5) // Limite le zoom max à 5x
      : Math.max(scale - scaleFactor, 0.5); // Limite le zoom min à 0.5x
    
    setScale(newScale);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scale, isOpen]);

  // Gérer le début du déplacement de l'image
  const handleMouseDown = (e) => {
    if (e.button !== 0 || scale <= 1) return; // Seulement le clic gauche et si l'image est zoomée
    
    setIsDragging(true);
    setStartDragPosition({ x: e.clientX, y: e.clientY });
    setStartImagePosition({ x: position.x, y: position.y });
    
    // Désactiver la sélection de texte pendant le déplacement
    document.body.style.userSelect = 'none';
  };

  // Gérer le déplacement de l'image
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const dx = e.clientX - startDragPosition.x;
    const dy = e.clientY - startDragPosition.y;
    
    setPosition({
      x: startImagePosition.x + dx,
      y: startImagePosition.y + dy
    });
  };

  // Gérer la fin du déplacement de l'image
  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = '';
  };

  // Gérer le clic sur l'arrière-plan pour fermer le visualiseur
  const handleBackdropClick = (e) => {
    // Fermer seulement si le clic est sur l'arrière-plan, pas sur l'image
    if (e.target === containerRef.current) {
      onClose();
    }
  };

  // Fonctions de zoom
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.5, 0.5));
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Fonction de téléchargement de l'image
  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = src;
    link.download = downloadFilename || 'image';
    link.click();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="iv-container" 
      ref={containerRef}
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Barre d'outils supérieure */}
      <div className="iv-header">
        <div className="iv-tools">
          <button className="iv-tool-button" onClick={zoomIn} title="Zoom avant">
            <FaSearchPlus />
          </button>
          <button className="iv-tool-button" onClick={zoomOut} title="Zoom arrière">
            <FaSearchMinus />
          </button>
          <button className="iv-tool-button" onClick={resetZoom} title="Réinitialiser le zoom">
            <FaSearch />
          </button>
        </div>
        <button className="iv-close-button" onClick={onClose} title="Fermer">
          <FaTimes />
        </button>
      </div>
      
      {/* Conteneur d'image */}
      <div 
        className={`iv-image-container ${isDragging ? 'iv-dragging' : ''}`}
        style={{ cursor: scale > 1 ? 'grab' : 'default' }}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="iv-image"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease'
          }}
          onMouseDown={handleMouseDown}
          onClick={(e) => e.stopPropagation()} // Empêcher la fermeture lors d'un clic sur l'image
          draggable="false"
        />
      </div>
      
      {/* Gros bouton FERMER très visible */}
      <button 
        className="iv-exit-button"
        onClick={onClose}
      >
        FERMER <FaTimes />
      </button>
      
      {/* Informations sur l'image */}
      <div className="iv-info">
        <div className="iv-zoom-info">Zoom: {Math.round(scale * 100)}%</div>
        {alt && <div className="iv-image-alt">{alt}</div>}
      </div>
    </div>
  );
};

export default ImageViewer;
export { ImageViewer };